import { isEmpty, noop } from "lodash";
import { useEffect, useMemo, useRef } from "react";
import { type FieldErrors, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import type { InvoiceData } from "@/apis/gql/types";
import { DatePickerInput } from "@/components/shared/form/date-picker-input";
import { AutoSaveForm } from "@/components/shared/form/form-auto-save";
import { FormNumberInput } from "@/components/shared/form/form-number-input";
import { SelectInput } from "@/components/shared/form/select-input";
import { ValidationErrorMessage } from "@/components/shared/form/shared/form-validation";
import { TextInput } from "@/components/shared/form/text-input";
import { Form } from "@/components/ui/form";
import { TypographySmall } from "@/components/ui/typography";
import { currencyCodes } from "@/lib/constants/constants";
import { EVENT_NAMES } from "@/lib/events/names";
import { confColor } from "@/lib/miscellaneous/confidence-color";
import { cn, isEuInvoice } from "@/lib/utils";
import { useUpdateInvoice } from "@/queries/invoices/update";

import { ReviewModes } from "../coreInvoiceForm/invoiceCoreInfoForm";

const formSchema = z.object({
  invoiceId: z.string().min(1).max(50),
  invoiceNo: z.string().max(50).nullable(),
  totalAmount: z.number().nullable(),
  currency: z.string().min(1).max(3).nullable(),
  invoiceDate: z.date().nullable(),
  serviceDate: z.date().nullable(),
  vatId: z.string().max(20).nullable(),
});

type FormValues = z.infer<typeof formSchema>;

type Props = {
  invoice: InvoiceData;
  disabled?: string | boolean;
  readOnly?: string | React.ReactNode | boolean;
  reviewMode: ReviewModes;
  onFieldFocus?: ({ field, value }: { field: string; value: unknown }) => void;
  onFieldBlur?: () => void;
};

export const InvoiceCoreInfo = ({
  invoice,
  disabled,
  readOnly,
  onFieldFocus = noop,
  onFieldBlur = noop,
  reviewMode,
}: Props) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);

  const [updateInvoice] = useUpdateInvoice();

  const currencyOptions = useMemo(
    () =>
      currencyCodes.map((currency) => {
        return {
          label: currency,
          value: currency,
        };
      }),
    [],
  );

  const values = useMemo<FormValues>(
    () => ({
      invoiceId: invoice.invoiceId,
      invoiceNo: invoice.invoiceNo,
      totalAmount: invoice.totalAmount,
      currency: invoice.currency,
      invoiceDate: invoice.invoiceDate ? new Date(invoice.invoiceDate) : null,
      serviceDate: invoice.serviceDate ? new Date(invoice.serviceDate) : null,
      vatId: invoice.vatId || null,
    }),
    [invoice],
  );

  const form = useForm<FormValues>({
    defaultValues: values,
    shouldFocusError: false,
    resetOptions: {
      keepErrors: true,
      keepValues: true,
    },
  });

  const formInvoiceId = form.watch("invoiceId");

  useEffect(() => {
    form.reset(values, { keepValues: false, keepErrors: false });
    form.trigger(); //trigger validations
  }, [formInvoiceId, form.reset, form.trigger]);

  const handleTotalAmountBlur = (event: any) => {
    onFieldBlur();

    if (event === values.totalAmount) {
      return;
    }

    const _event = new CustomEvent(EVENT_NAMES.TOTAL_AMOUNT_CHANGED, {
      detail: { value: event },
    });

    window.dispatchEvent(_event);
  };

  const handleSubmit = async (invoice: FormValues) => {
    try {
      const { formState } = form;

      const dirtyFields = formState.dirtyFields;

      const updatedFields = Object.keys(invoice).reduce<any>(
        (acc: any, key: any) => {
          //@ts-ignore typescript is not happy with this
          if (dirtyFields[key]) {
            //@ts-ignore typescript is not happy with this
            const value = invoice[key];

            acc[key] = value;
            return acc;
          }
          return acc;
        },
        {},
      );

      if (!isEmpty(updatedFields)) {
        await updateInvoice({ invoiceId: invoice.invoiceId, ...updatedFields });
        for (const key in updatedFields) {
          form.resetField(key as any, { defaultValue: updatedFields[key] });
        }
      }
    } catch (error) {}
  };

  const handleError = (errors: FieldErrors) => {
    const errorKeys = Object.keys(errors);
    const values = form.getValues();

    const validValues = Object.keys(values).reduce((acc: any, key: any) => {
      if (!errorKeys.includes(key)) {
        //@ts-ignore typescript is not happy with this
        acc[key] = values[key];
      }
      return acc;
    }, {});

    handleSubmit(validValues);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  return (
    <Form {...form}>
      <form ref={formRef} className="grid gap-2" onKeyDown={handleKeyDown}>
        {reviewMode === ReviewModes.TASK ? null : isEuInvoice(invoice) ? (
          <div className="grid grid-cols-12 items-center">
            <div className="col-span-3">
              <TypographySmall>{t("vat_id")}</TypographySmall>
            </div>
            <div className="col-span-9">
              <TextInput
                readOnly={readOnly}
                disabled={disabled}
                name="vatId"
                onFocus={(value) => {
                  onFieldFocus({ field: "vatId", value });
                }}
                onBlur={onFieldBlur}
                className={cn(confColor(invoice.vatIdConfidence))}
              />
              <ValidationErrorMessage name="vatId" />
            </div>
          </div>
        ) : null}
        {invoice.invoiceNoConfidence &&
        reviewMode === ReviewModes.TASK ? null : (
          <div className="grid grid-cols-12 items-center">
            <div className="col-span-3">
              <TypographySmall>{t("invoice_no")}</TypographySmall>
            </div>
            <div className="col-span-9">
              <TextInput
                readOnly={readOnly}
                disabled={disabled}
                name="invoiceNo"
                onFocus={(value) => {
                  onFieldFocus({ field: "invoiceNo", value });
                }}
                onBlur={onFieldBlur}
                className={cn(confColor(invoice.invoiceNoConfidence))}
              />
              <ValidationErrorMessage name="invoiceNo" />
            </div>
          </div>
        )}
        {invoice.invoiceDateConfidence &&
        reviewMode === ReviewModes.TASK ? null : (
          <div className="grid grid-cols-12 items-center">
            <div className="col-span-3">
              <TypographySmall>{t("invoice_date")}</TypographySmall>
            </div>
            <div className="col-span-9">
              <div className="w-36">
                <DatePickerInput
                  disabled={disabled}
                  readOnly={readOnly}
                  name="invoiceDate"
                  onFocus={(value) => {
                    onFieldFocus({ field: "invoiceDate", value });
                  }}
                  onBlur={onFieldBlur}
                  className={cn(confColor(invoice.invoiceDateConfidence))}
                />
              </div>
              <ValidationErrorMessage name="invoiceDate" />
            </div>
          </div>
        )}
        {invoice.serviceDateConfidence &&
        reviewMode === ReviewModes.TASK ? null : (
          <div className="grid grid-cols-12 items-center">
            <div className="col-span-3">
              <TypographySmall>{t("service_date")}</TypographySmall>
            </div>
            <div className="col-span-9">
              <div className="w-36">
                <DatePickerInput
                  disabled={disabled}
                  readOnly={readOnly}
                  name="serviceDate"
                  onFocus={(value) => {
                    onFieldFocus({ field: "serviceDate", value });
                  }}
                  onBlur={onFieldBlur}
                  className={cn(confColor(invoice.serviceDateConfidence))}
                />
              </div>
              <ValidationErrorMessage name="serviceDate" />
            </div>
          </div>
        )}
        {invoice.totalAmountConfidence &&
        invoice.currency &&
        reviewMode === ReviewModes.TASK ? null : (
          <div className="grid grid-cols-12 items-center">
            <div className="col-span-3">
              <TypographySmall>{t("total_amount")}</TypographySmall>
            </div>
            <div className="col-span-9">
              <div className="flex flex-row gap-1 items-end">
                <div className="w-36">
                  <FormNumberInput
                    disabled={disabled}
                    readOnly={readOnly}
                    name="totalAmount"
                    onFocus={(value) => {
                      onFieldFocus({ field: "totalAmount", value });
                    }}
                    onBlur={handleTotalAmountBlur}
                    className={cn(
                      "text-right",
                      confColor(invoice.totalAmountConfidence),
                    )}
                  />
                  <ValidationErrorMessage name="totalAmount" />
                </div>
                <div className="self-start">
                  <SelectInput
                    disabled={disabled}
                    readOnly={readOnly}
                    name="currency"
                    options={currencyOptions}
                    className={cn(
                      confColor(
                        invoice.totalAmountConfidence && invoice.currency
                          ? 1
                          : invoice.totalAmountConfidence === 0
                            ? 0
                            : undefined,
                      ),
                      "w-20",
                    )}
                  />
                  <ValidationErrorMessage name="currency" />
                </div>
              </div>
            </div>
          </div>
        )}
        <AutoSaveForm onSubmit={handleSubmit} immediate />
      </form>
    </Form>
  );
};
